import * as React from 'react'

import { Box, type LabeledInputProps, Text } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import { reaction } from 'mobx'

import { observer } from 'mobx-react'

import InputText from 'modules/core/components/Inputs/Text'
import i18n from 'modules/core/lib/i18n'

import { ProfileEmployeeData } from 'modules/profile/components/ProfileForm/types'

interface Props {
  form: Form
  employeeData: ProfileEmployeeData
  employeeExists: boolean
  isCurrentUser: boolean
  canEditEmployeeEmail?: boolean
  readonly?: boolean
  autofocus?: boolean
  mandatory?: boolean
  secondaryLabel?: LabeledInputProps['secondaryLabel']
}

const Email = ({
  employeeData,
  form,
  readonly,
  employeeExists,
  isCurrentUser,
  canEditEmployeeEmail,
  secondaryLabel,
  mandatory,
  autofocus,
}: Props) => {
  const isReadOnly = React.useMemo(() => {
    const canNotEdit = employeeExists
      ? !Boolean(canEditEmployeeEmail)
      : Boolean(!isCurrentUser && employeeData.joined)

    return readonly || canNotEdit
  }, [employeeData, readonly, employeeExists, canEditEmployeeEmail, isCurrentUser])

  React.useEffect(() => {
    const handler = reaction(
      () => employeeData.unconfirmedEmail,
      () => {
        const email = form.get('email')
        email.set(employeeData.loginEmail || '')
        email.clean()

        const uncomfirmedEmail = form.get('unconfirmed_email')
        uncomfirmedEmail.set(employeeData.unconfirmedEmail)
        uncomfirmedEmail.clean()
      },
      {
        fireImmediately: true,
      }
    )

    return () => {
      if (handler) {
        handler()
      }
    }
  }, [employeeData, form])

  const renderResendInfo = () => {
    const userIsInvited = !!employeeData.access.invitedOn
    const unconfirmedEmail = employeeData.unconfirmedEmail
    const equalEmails = unconfirmedEmail === employeeData.access.email

    if (!userIsInvited || !unconfirmedEmail || equalEmails) {
      return null
    }

    return (
      <>
        <Text size="200">
          {i18n.t('employee_form.unconfirmed_description', {
            email: unconfirmedEmail,
          })}
        </Text>
      </>
    )
  }

  const bangText: boolean | undefined = isReadOnly || mandatory

  return (
    <Box gap="s8">
      <InputText
        readonly={isReadOnly}
        field={form.get('email')}
        value={form.get('email').value}
        label={i18n.t('employee_form.email')}
        mandatory={bangText}
        autofocus={autofocus}
        secondaryLabel={secondaryLabel}
      />
      {renderResendInfo()}
    </Box>
  )
}

export default observer(Email)
